// App.tsx - Complete with AuthProvider, NetworkProvider AND Maintenance Mode
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { NetworkProvider, useNetwork } from './context/NetworkContext';
import { ref, onValue } from 'firebase/database';
import { db } from './firebase';
import NetworkError from './components/NetworkError';
import logo from './assets/logo.png';
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Singnup';
import Dashboard from './pages/Dashboard';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Refund from './pages/Refund';
import FairPlay from './pages/FairPlay';
import GameScreen from './pages/GameScreen';
import Guidelines from './pages/Guidelines';
import Download from './pages/Download';
import FAQ from './pages/FAQ';
import About from './pages/About';
import Wallet from './pages/Wallet';
import Profile from './pages/Profile';
import Leaderboard from './pages/Leaderboard';
import TournamentDetails from './pages/TournamentDetails';
import MyTeam from './pages/MyTeam';
import AccountSuspended from './pages/AccountSuspended';
import Support from './pages/Support';
import Rules from './pages/Rules';
import PaymentReturn from './pages/PaymentReturn';

// Loading Component for reuse
const LoadingSpinner = () => (
  <div className="min-h-screen bg-gradient-to-br from-[#0A0A0F] to-[#0F0F1A] flex items-center justify-center overflow-hidden">
    <div className="absolute w-60 h-60 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
    <div className="absolute w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ top: '20%', left: '10%' }} />
    <div className="absolute w-56 h-56 bg-pink-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ bottom: '20%', right: '10%' }} />
    
    <div className="relative flex flex-col items-center">
      <div className="relative w-28 h-28">
        <div className="absolute inset-0 rounded-full">
          <svg className="w-full h-full animate-spin-slow" viewBox="0 0 100 100">
            <defs>
              <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#A855F7" />
                <stop offset="50%" stopColor="#3B82F6" />
                <stop offset="100%" stopColor="#EC4899" />
              </linearGradient>
            </defs>
            <circle
              cx="50"
              cy="50"
              r="44"
              fill="none"
              stroke="url(#ringGradient)"
              strokeWidth="3"
              strokeDasharray="200"
              strokeDashoffset="50"
              strokeLinecap="round"
            />
          </svg>
        </div>
        
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-blue-500/20 to-pink-500/20 blur-xl rounded-full animate-pulse" />
        <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-pink-500/10 rounded-full blur-md animate-pulse-slow" />
        
        <div className="absolute inset-0 flex items-center justify-center">
          <img
            src={logo}
            alt="ArenaX"
            className="w-20 h-20 object-contain animate-float"
          />
        </div>
      </div>

      <div className="mt-5 text-center">
        <p className="text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text font-rajdhani font-bold text-base tracking-widest animate-pulse">
          LOADING ARENA...
        </p>
        <div className="flex items-center justify-center gap-1.5 mt-2">
          <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-bounce" style={{ animationDelay: '0s' }} />
          <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0.2s' }} />
          <div className="w-1.5 h-1.5 rounded-full bg-pink-500 animate-bounce" style={{ animationDelay: '0.4s' }} />
        </div>
      </div>
    </div>
  </div>
);

// 🆕 Maintenance Screen Component
const MaintenanceScreen = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A0A0F] to-[#0F0F1A] flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        <div className="mx-auto mb-8 w-24 h-24 rounded-full bg-purple-500/10 border border-purple-500/30 flex items-center justify-center">
          <svg
            className="w-12 h-12 text-purple-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 9v3.75m0 3.75h.008M10.29 3.86l-8.02 14A2 2 0 004 21h16a2 2 0 001.73-3.14l-8.02-14a2 2 0 00-3.42 0z"
            />
          </svg>
        </div>

        <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
          Website Under Maintenance
        </h1>

        <p className="text-gray-400 text-base md:text-lg leading-relaxed">
          ArenaX is currently undergoing maintenance.
          Please check back again shortly.
        </p>

        <div className="mt-8 flex justify-center gap-2">
          <span className="w-2 h-2 rounded-full bg-purple-500 animate-bounce" />
          <span
            className="w-2 h-2 rounded-full bg-blue-500 animate-bounce"
            style={{ animationDelay: '0.15s' }}
          />
          <span
            className="w-2 h-2 rounded-full bg-pink-500 animate-bounce"
            style={{ animationDelay: '0.3s' }}
          />
        </div>
      </div>
    </div>
  );
};

// 🆕 Maintenance Guard Component
const MaintenanceGuard = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isMaintain, setIsMaintain] = useState(true);
  const [maintenanceLoading, setMaintenanceLoading] = useState(true);

  useEffect(() => {
    const maintenanceRef = ref(db, 'AppDetails/isMaintain');

    const unsubscribe = onValue(
      maintenanceRef,
      (snapshot) => {
        const value = snapshot.val();


        // 🔑 Rule:
        // true  = website normal
        // false = maintenance mode
        setIsMaintain(value !== false);
        setMaintenanceLoading(false);
      },
      (error) => {
        console.error('❌ Maintenance listener error:', error);

        // Fail-safe: If we cannot read the setting, keep the website accessible.
        setIsMaintain(true);
        setMaintenanceLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  if (maintenanceLoading) {
    return <LoadingSpinner />;
  }

  if (!isMaintain) {
    return <MaintenanceScreen />;
  }

  return <>{children}</>;
};

// Protected Route Component using useAuth
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public Route Component using useAuth
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return <>{children}</>;
};

// Main App Content with Network handling AND Maintenance Guard
const AppContent = () => {
  const { isOnline, retryConnection } = useNetwork();

  // Show network error if offline
  if (!isOnline) {
    return <NetworkError onRetry={retryConnection} />;
  }

  return (
    <Router>
      {/* 🆕 Maintenance Guard wraps ALL routes */}
      <MaintenanceGuard>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } 
          />
          <Route 
            path="/signup" 
            element={
              <PublicRoute>
                <Signup />
              </PublicRoute>
            } 
          />
          <Route
            path="/payment-return"
            element={<PaymentReturn />}
          />
          
          {/* Protected Routes (Require Login) */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/my-team" 
            element={
              <ProtectedRoute>
                <MyTeam />
              </ProtectedRoute>
            } 
          />
          <Route path="/rules" element={<Rules />} />
          
          {/* Game Routes */}
          <Route 
            path="/game/:game" 
            element={
              <ProtectedRoute>
                <GameScreen />
              </ProtectedRoute>
            } 
          />
          
          {/* Tournament Routes */}
          <Route 
            path="/tournament/:game/:matchId" 
            element={
              <ProtectedRoute>
                <TournamentDetails />
              </ProtectedRoute>
            } 
          />
          
          {/* Dashboard Sub-routes */}
          <Route 
            path="/dashboard/wallet" 
            element={
              <ProtectedRoute>
                <Wallet />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/profile" 
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/leaderboard" 
            element={
              <ProtectedRoute>
                <Leaderboard />
              </ProtectedRoute>
            } 
          />
          
          {/* Legal & Information Pages */}
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/refund" element={<Refund />} />
          <Route path="/fairplay" element={<FairPlay />} />
          <Route path="/guidelines" element={<Guidelines />} />
          <Route path="/download" element={<Download />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/about" element={<About />} />
          <Route path="/support" element={<Support />} />
          <Route path="/account-suspended" element={<AccountSuspended />} />
          
          {/* 404 Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
       
        {/* Global styles for animations */}
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@300;400;500;600;700&display=swap');
          
          .font-rajdhani {
            font-family: 'Rajdhani', sans-serif;
          }
          
          @keyframes spin-slow {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-8px); }
          }
          
          @keyframes pulse-slow {
            0%, 100% { 
              opacity: 0.3;
              transform: scale(1);
            }
            50% { 
              opacity: 0.6;
              transform: scale(1.1);
            }
          }
          
          @keyframes bounce {
            0%, 100% { 
              transform: translateY(0);
            }
            50% { 
              transform: translateY(-6px);
            }
          }
          
          .animate-spin-slow {
            animation: spin-slow 1.5s linear infinite;
          }
          
          .animate-float {
            animation: float 2.5s ease-in-out infinite;
          }
          
          .animate-pulse-slow {
            animation: pulse-slow 3s ease-in-out infinite;
          }
          
          .animate-bounce {
            animation: bounce 0.8s ease-in-out infinite;
          }
        `}</style>
      </MaintenanceGuard>
    </Router>
  );
};

// Main App component wrapped with AuthProvider AND NetworkProvider
function App() {
  return (
    <AuthProvider>
      <NetworkProvider>
        <AppContent />
      </NetworkProvider>
    </AuthProvider>
  );
}

export default App;